// cSpell:ignore Silae SILAE
import type { SilaeSourceFormData, SourceType } from "@/types/silae";
import type { SilaeFormatters } from "@/types/silae-formatters";
import { RichFormattedText } from "./RichFormattedText";

// ─── Palette par type de source ─────────────────────────────────────────────

interface TypePalette {
  varName: string;
  value: string;
}

const PALETTE: Record<SourceType, TypePalette> = {
  cumul_bulletins: {
    varName: "text-teal-600 dark:text-teal-400",
    value: "text-teal-800 dark:text-teal-300",
  },
  cumul_stock_var: {
    varName: "text-cyan-600 dark:text-cyan-400",
    value: "text-cyan-800 dark:text-cyan-300",
  },
  stored_variable: {
    varName: "text-violet-600 dark:text-violet-400",
    value: "text-violet-800 dark:text-violet-300",
  },
  questionnaire_variable: {
    varName: "text-pink-600 dark:text-pink-400",
    value: "text-pink-800 dark:text-pink-300",
  },
  fixed: {
    varName: "text-amber-600 dark:text-amber-400",
    value: "text-amber-800 dark:text-amber-300",
  },
  evp: {
    varName: "text-sky-600 dark:text-sky-400",
    value: "text-sky-800 dark:text-sky-300",
  },
  salary_base: {
    varName: "text-sky-600 dark:text-sky-400",
    value: "text-sky-800 dark:text-sky-300",
  },
  salary_gross: {
    varName: "text-sky-600 dark:text-sky-400",
    value: "text-sky-800 dark:text-sky-300",
  },
  salary_net: {
    varName: "text-sky-600 dark:text-sky-400",
    value: "text-sky-800 dark:text-sky-300",
  },
  advanced_variable: {
    varName: "text-fuchsia-600 dark:text-fuchsia-400",
    value: "text-fuchsia-800 dark:text-fuchsia-300",
  },
  manual_formula: {
    varName: "text-zinc-500 dark:text-zinc-400",
    value: "text-zinc-600 dark:text-zinc-400",
  },
  complex_expression: {
    varName: "text-indigo-600 dark:text-indigo-400",
    value: "text-indigo-800 dark:text-indigo-300",
  },
};

const FALLBACK_PALETTE: TypePalette = {
  varName: "text-zinc-500 dark:text-zinc-400",
  value: "text-zinc-600 dark:text-zinc-400",
};

const OP_SYMBOLS: Record<string, string> = {
  "+": "+",
  "-": "−",
  "*": "×",
  "/": "÷",
};

// ─── Structures de lignes de code ────────────────────────────────────────────

interface CodeLine {
  varName: string;
  sourceType: SourceType;
  description: string;
  isComplex: boolean;
}

// ─── Walker récursif ─────────────────────────────────────────────────────────

interface WalkResult {
  varName: string;
  lines: CodeLine[];
}

function walk(
  item: SilaeSourceFormData,
  pathKey: string,
  formatters: SilaeFormatters,
): WalkResult {
  const varName = item.variableName?.trim() || `VAR_${pathKey}`;
  const sourceType = item.sourceType;

  if (sourceType === "complex_expression") {
    const children = item.complexExpression ?? [];
    const childResults = children.map((child, idx) =>
      walk(child, `${pathKey}_${idx + 1}`, formatters),
    );
    const lines: CodeLine[] = [];
    for (const r of childResults) lines.push(...r.lines);

    const inner = children
      .map((child, idx) => {
        const seg = childResults[idx].varName;
        if (idx < children.length - 1) {
          const op =
            OP_SYMBOLS[child.mathOperator ?? "+"] ?? child.mathOperator ?? "+";
          return `${seg} ${op}`;
        }
        return seg;
      })
      .join(" ");

    lines.push({
      varName,
      sourceType,
      description: `(${inner})`,
      isComplex: true,
    });
    return { varName, lines };
  }

  const description = formatters.formatSourceDisplay(item, {
    skipEvpForce: true,
  });
  return {
    varName,
    lines: [{ varName, sourceType, description, isComplex: false }],
  };
}

// ─── Tokeniseur d'expressions ────────────────────────────────────────────────

function ExprTokens({
  expression,
  allLines,
}: {
  expression: string;
  allLines: CodeLine[];
}) {
  const typeByVar = new Map<string, SourceType>();
  for (const l of allLines) typeByVar.set(l.varName, l.sourceType);

  const tokens = expression.split(/(\b[A-Za-z_][A-Za-z0-9_]*\b)/);
  return (
    <span className="min-w-0 wrap-break-word">
      {tokens.map((tok, i) => {
        const st = typeByVar.get(tok);
        if (st) {
          const p = PALETTE[st] ?? FALLBACK_PALETTE;
          return (
            <span key={i} className={`font-semibold ${p.varName}`}>
              {tok}
            </span>
          );
        }
        return (
          <span key={i} className="text-zinc-500 dark:text-zinc-400">
            {tok}
          </span>
        );
      })}
    </span>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────────

interface ComplexExpressionTreeProps {
  source: SilaeSourceFormData;
  formatters: SilaeFormatters;
}

export function ComplexExpressionTree({
  source,
  formatters,
}: ComplexExpressionTreeProps) {
  const children = source.complexExpression ?? [];

  if (children.length === 0) {
    const fallback = formatters.formatSourceDisplay(source, {
      skipEvpForce: true,
    });
    return (
      <span className="text-zinc-400 dark:text-zinc-500 text-[10px] italic">
        {fallback}
      </span>
    );
  }

  const allLines: CodeLine[] = [];
  const topNames: string[] = [];

  children.forEach((child, idx) => {
    const result = walk(child, String(idx + 1), formatters);
    allLines.push(...result.lines);
    topNames.push(result.varName);
  });

  const resultatExpr = children
    .map((child, idx) => {
      const seg = topNames[idx];
      if (idx < children.length - 1) {
        const op =
          OP_SYMBOLS[child.mathOperator ?? "+"] ?? child.mathOperator ?? "+";
        return `${seg} ${op}`;
      }
      return seg;
    })
    .join(" ");

  return (
    <div className="font-mono text-[10px] leading-relaxed space-y-0.5">
      {allLines.map((line, idx) => {
        const p = PALETTE[line.sourceType] ?? FALLBACK_PALETTE;
        return (
          <div key={idx} className="flex gap-1 min-w-0">
            <span className={`shrink-0 font-semibold ${p.varName}`}>
              {line.varName}
            </span>
            <span className="shrink-0 text-zinc-400 dark:text-zinc-500">=</span>
            {line.isComplex ? (
              <ExprTokens expression={line.description} allLines={allLines} />
            ) : (
              <span className={`min-w-0 wrap-break-word ${p.value}`}>
                <RichFormattedText text={line.description} />
              </span>
            )}
          </div>
        );
      })}
      <div className="flex gap-1 min-w-0 mt-1 pt-1 border-t border-zinc-200 dark:border-zinc-700">
        <span className="shrink-0 font-semibold text-zinc-500 dark:text-zinc-400">
          Resultat
        </span>
        <span className="shrink-0 text-zinc-400 dark:text-zinc-500">=</span>
        <ExprTokens expression={resultatExpr} allLines={allLines} />
      </div>
    </div>
  );
}
